import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Typography,
  Button,
  Stack,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Card,
  CardContent,
} from "@altimateai/lego";
import { IconSparkles } from "@tabler/icons-react";
import { DateRange } from "@/components/dateRange";
import dayjs from "dayjs";
import { BackButton } from "@/components/button";
import {
  useModelServingEndpointDetail,
  useModelServingEndpointTrend,
  useModelServingEndpointCallers,
  useAIFunctionUsageBreakdown,
} from "../../hooks";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { ModelServingEndpointTrendChart } from "./ModelServingEndpointTrendChart";
import EndpointUsageTable from "./EndpointUsageTable";
import { FlexListSummary } from "@/components/listSummary";
import { KeyInfoGrid } from "@/components/keyInfo";
import { formatCost } from "@/helpers/utils";
import { formatNumber } from "@/uiCore/components/formatters/format";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { getServingTypeLabel } from "../utils";

const ModelServingEndpointDetail = (): JSX.Element => {
  const { endpointId: endpointName } = useParams<{ endpointId: string }>();
  useBreadcrumb(endpointName ? decodeURIComponent(endpointName) : "Model Serving Endpoint", {
    defaultParents: [{ label: "AI/ML", pathname: "/ai-ml" }],
  });
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const { currency } = useAppState();

  const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
  const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
  } = useModelServingEndpointDetail(
    {
      endpoint_name: endpointName || "",
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    },
    Boolean(endpointName)
  );

  const { data: trendData, isLoading: trendLoading } = useModelServingEndpointTrend(
    {
      endpoint_name: endpointName || "",
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    },
    Boolean(endpointName)
  );

  const isAIFunction = detail?.entity_type === "AI_FUNCTION";

  const { data: callersData, isLoading: callersLoading } = useModelServingEndpointCallers(
    {
      endpoint_name: endpointName || "",
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      limit: 10,
    },
    Boolean(endpointName)
  );

  const { data: usageBreakdownData, isLoading: usageBreakdownLoading } =
    useAIFunctionUsageBreakdown(
      {
        endpoint_name: endpointName || "",
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      },
      Boolean(endpointName) && isAIFunction
    );

  const usageLoading = isAIFunction ? usageBreakdownLoading || callersLoading : callersLoading;
  const summary = useMemo(
    () => [
      {
        label: "Total Cost",
        value: detail ? formatCost(detail.total_cost_usd, currency) : "-",
      },
      {
        label: "Total Invocations",
        value: detail ? formatNumber(detail.total_requests) : "-",
      },
      { label: "Unique Users", value: detail ? formatNumber(detail.unique_users) : "-" },
    ],
    [detail, currency]
  );

  const keyInfoItems = useMemo(() => {
    if (!detail) return [];
    const items: { label: string; value: string }[] = [{ label: "Status", value: "Active" }];
    if (detail.entity_type)
      items.push({ label: "Type", value: getServingTypeLabel(detail.entity_type) });
    if (detail.entity_name) items.push({ label: "Entity", value: detail.entity_name });
    if (detail.created_by) items.push({ label: "Created by", value: detail.created_by });
    if (detail.usage_source) items.push({ label: "Source", value: detail.usage_source });
    return items;
  }, [detail]);

  if (!detailLoading && (detailError || !detail)) {
    return (
      <div className="al-p-4">
        <Stack
          direction="column"
          gap="md"
          align="center"
          justify="center"
          className="al-min-h-[400px]"
        >
          <Typography variant="h4" className="al-text-red-500">
            Error loading endpoint details
          </Typography>
          <Typography className="al-text-gray-500">
            Please try again later or contact support if the problem persists.
          </Typography>
          <Button asChild>
            <Link to="/ai-ml">Back to AI/ML Services</Link>
          </Button>
        </Stack>
      </div>
    );
  }

  return (
    <Stack className="al-p-4" direction="column" gap="md">
      <Stack direction="row" align="center" justify="between">
        <Stack direction="row" align="center" gap="sm">
          <BackButton defaultUrl="/ai-ml" />
          {detailLoading ? (
            <div className="al-h-8 al-w-64 al-rounded-md al-animate-pulse al-bg-gray-15" />
          ) : (
            <Typography variant="page-heading">{detail?.endpoint_name}</Typography>
          )}
        </Stack>
        <DateRange
          startDate={startDate}
          endDate={endDate}
          onDateRangeSelect={onUpdate}
          id="endpoint-detail-date-range"
          maxDate={new Date()}
        />
      </Stack>

      <KeyInfoGrid
        title="Key Information"
        ariaLabel="Entity metadata"
        items={keyInfoItems}
        isLoading={detailLoading}
      />

      <FlexListSummary summary={summary} isLoading={detailLoading} skeletonCount={3} />

      <ModelServingEndpointTrendChart
        data={trendData ?? []}
        isLoading={trendLoading}
        entityType={detail?.entity_type}
      />

      <Card>
        <CardContent className="!al-pt-6">
          <Tabs variant="underlined" defaultValue="usage">
            <TabsList>
              <TabsTrigger value="usage">Endpoint Usage</TabsTrigger>
              <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
            </TabsList>
            <TabsContent value="usage">
              <EndpointUsageTable
                callersData={callersData}
                usageBreakdownData={usageBreakdownData}
                isAIFunction={isAIFunction}
                isLoading={usageLoading}
              />
            </TabsContent>
            <TabsContent value="opportunities">
              <Stack direction="column" gap="md" align="center" className="al-py-12">
                <IconSparkles className="al-h-12 al-w-12 al-text-gray-300" />
                <Typography variant="h4" className="al-text-gray-700">
                  No opportunities yet
                </Typography>
                <Typography className="al-text-gray-500">
                  We&apos;ll surface cost-saving suggestions for this endpoint once enabled.
                </Typography>
                <Stack direction="row" align="center" gap="sm">
                  <Switch disabled aria-label="Find Opportunities" />
                  <Typography size="sm" className="al-text-gray-700">
                    Find Opportunities
                  </Typography>
                </Stack>
              </Stack>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ModelServingEndpointDetail;
